import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw, MessageSquare, ExternalLink,QrCode } from 'lucide-react';
import AdminLayout from '@/components/Admin/Layout/AdminLayout';
import ModuleLayout from '@/layouts/ModuleLayout';
import FeedbackList from '@/components/Admin/Customer_Master/FeedbackList';
import { useAuth } from '@/context/AuthContext';
import { P } from '@/config/permissionCodes';
import branchService from '@/services/branchService';
import toast from 'react-hot-toast';
import FeedbackQRModal from '@/components/Admin/Customer_Master/FeedbackQRModal';

export default function FeedbackManagementPage() {
  const [componentActions, setComponentActions] = useState(null);
  const [spinning, setSpinning]                 = useState(false);
  const [branches, setBranches]                 = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [showQR, setShowQR]                     = useState(false);

  const location = useLocation();
  const { hasPermission, user } = useAuth();

  const isOwner       = user?.userType === 'SUPER_USER';
  const staffBranchId = user?.branchId ?? user?.branch?.id ?? user?.defaultBranchId;
  const feedbackBranchId = isOwner ? selectedBranchId : staffBranchId;

  const canView    = hasPermission(P.CRM.CUSTOMERS.SCREEN);
  const isCRMRoute = location.pathname.startsWith('/crm');
  const Layout     = isCRMRoute ? ModuleLayout : AdminLayout;

  useEffect(() => {
    if (isOwner) {
      branchService.getAllBranches()
        .then(res => {
          const list = res?.data || res || [];
          setBranches(list);
        })
        .catch(() => {});
    }
  }, [isOwner]);

  const handleActionsReady = useCallback((actions) => {
    setComponentActions(actions);
  }, []);

  const handleRefresh = () => {
    if (!componentActions?.onRefresh) return;
    setSpinning(true);
    componentActions.onRefresh();
    setTimeout(() => setSpinning(false), 800);
  };

  const handleOpenFeedbackForm = () => {
    if (!feedbackBranchId) {
      toast.error(isOwner ? 'Please select a branch first' : 'No branch found on your account');
      return;
    }
    window.open(`/feedback/${feedbackBranchId}`, '_blank');
  };

  const handleOpenQR = () => {
    if (!feedbackBranchId) {
      toast.error(isOwner ? 'Please select a branch first' : 'No branch found');
      return;
    }
    setShowQR(true);
  };

  if (!canView) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full text-center px-6 py-20">
          <div className="space-y-2">
            <div className="w-14 h-14 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-6 h-6 text-rose-400" />
            </div>
            <p className="text-base font-semibold text-gray-700">Access Denied</p>
            <p className="text-sm text-gray-400">You don't have permission to view customer feedback.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col h-full">

        {/* ── Sticky Header ── */}
        <div
          className="sticky top-0 z-10 border-b border-gray-200/80 px-6 py-4 flex items-center justify-between"
          style={{
            background: "linear-gradient(to right, #ffffff, #fff8f8)",
            backdropFilter: "blur(8px)",
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg,#f43f5e,#fb923c)", boxShadow: "0 2px 10px rgba(244,63,94,.25)" }}
            >
              <MessageSquare size={16} className="text-white" />
            </div>
            <div>
              <h1 className="text-[15px] font-bold text-gray-900 leading-tight">Customer Feedback</h1>
              <p className="text-[11px] text-gray-400 mt-0.5">Track reviews, ratings & customer sentiment</p>
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-2"> 

            {/* Refresh */}
            {componentActions && (
              <button
                onClick={handleRefresh}
                title="Refresh"
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 shadow-sm transition-all"
              >
                <RefreshCw size={14} className={spinning ? "animate-spin text-rose-500" : ""} />
                <span className="hidden sm:inline text-xs font-medium">Refresh</span>
              </button>
            )}

            {/* Open public feedback form */}
            <button
              onClick={handleOpenFeedbackForm}
              disabled={isOwner && !selectedBranchId}
              title="Open public feedback form"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ExternalLink size={14} />
              <span className="hidden sm:inline">Feedback Form</span>
            </button>

            {/* QR Code */}
            <button
              onClick={handleOpenQR}
              disabled={isOwner && !selectedBranchId}
              title="Generate QR code for feedback form"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-rose-500 bg-rose-50 border border-rose-100 rounded-xl hover:bg-rose-100 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <QrCode size={14} />
              <span className="hidden sm:inline">QR Code</span>
            </button>

          </div>
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto">
          <FeedbackList renderActions={handleActionsReady} />
        </div>

      </div>

      {/* ── QR Modal ── */}
      {showQR && feedbackBranchId && (
        <FeedbackQRModal branchId={feedbackBranchId} onClose={() => setShowQR(false)} />
      )}

    </Layout>
  );
}